import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  SearchMenu,
  User,
  WindowSidebarLeftBlank18,
} from 'shared/assets/icons';
import { UserContextMenu } from 'shared/components/connected/user-context-menu';
import { Icon } from 'shared/components/ui/icon';
import { IconButton, Sizes } from 'shared/components/ui/icon-button';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Image } from 'shared/types/image';

import * as Styled from './user-button.style';

export type UserButtonViewProps = {
  name: string;
  image?: Image;
  onCloseSideNavigation?: () => void;
  onCloseMobileSideNavigation?: () => void;
  onSearch?: () => void;
  newVersionAvailable?: boolean;
};

export const UserButtonView: React.FC<UserButtonViewProps> = ({
  name,
  image,
  onCloseSideNavigation,
  onCloseMobileSideNavigation,
  onSearch,
  newVersionAvailable,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const closeSideNav = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onCloseSideNavigation?.();
  };

  const _onSearch = (e: React.MouseEvent) => {
    // this to prevent the button from being focussed after other inputs being unfocused (such as the search dialog)
    (e.currentTarget as HTMLElement)?.blur?.();
    e.preventDefault();
    e.stopPropagation();
    onCloseMobileSideNavigation?.();
    onSearch?.();
  };

  const _onClose = () => setTimeout(() => closeMenu(), 0);

  return (
    <Styled.Container ref={containerRef}>
      <Styled.Button
        onClick={menuOpen ? closeMenu : openMenu}
        tabIndex={0}
        $hasSideNavButton={!!onCloseSideNavigation}
      >
        <Styled.ButtonContent>
          <Styled.AvatarContainer>
            {image ? (
              <Styled.Image src={image.url} />
            ) : (
              <Styled.IconContainer>
                <Icon icon={User} />
              </Styled.IconContainer>
            )}
            {newVersionAvailable && <Styled.NewVersionBadge />}
          </Styled.AvatarContainer>

          <Styled.Name>{name}</Styled.Name>
        </Styled.ButtonContent>

        <Styled.ChevronContainer $open={menuOpen}>
          <Icon icon={ChevronLeft} />
        </Styled.ChevronContainer>
      </Styled.Button>

      <Styled.Interactions>
        {!!onSearch && (
          <IconButton
            size={Sizes.Medium}
            icon={SearchMenu}
            onClick={_onSearch}
            aria-label={t('general.menu.labels.search')}
          />
        )}

        {onCloseSideNavigation && (
          <Styled.CloseButtonContainer>
            <IconButton
              size={Sizes.Medium}
              icon={WindowSidebarLeftBlank18}
              onClick={closeSideNav}
              aria-label={t('general.menu.labels.close')}
            />
          </Styled.CloseButtonContainer>
        )}
      </Styled.Interactions>

      {menuOpen && (
        <UserContextMenu onClose={_onClose} referenceElement={containerRef} />
      )}
    </Styled.Container>
  );
};
